import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  Output,
  ViewChild,
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { environment } from "../../../environments/environment";

interface AltchaWidgetElement extends HTMLElement {
  configure(options: Record<string, unknown>): void | Promise<void>;
  reset(): void;
}

interface AltchaStateChangeEvent
  extends CustomEvent<{ payload: string | null; state: string }> {}

@Component({
    selector: "app-altcha",
    templateUrl: "./altcha.component.html",
    styleUrls: ["./altcha.component.scss"],
    standalone: false
})
export class AltchaComponent implements AfterViewInit, OnDestroy {
  private static readonly scriptLoads = new Map<string, Promise<void>>();

  @Input({ required: true }) action!: string;
  @Output() tokenChange = new EventEmitter<string | null>();
  @Output() readyChange = new EventEmitter<boolean>();
  @ViewChild("widget", { static: true }) widget!: ElementRef<AltchaWidgetElement>;

  private destroyed = false;
  private listenerAttached = false;
  status: "loading" | "ready" | "error" = "loading";

  private readonly stateChangeHandler = (event: Event) => {
    const { payload, state } = (event as AltchaStateChangeEvent).detail;
    const verified = state === "verified" && !!payload;
    this.zone.run(() => {
      this.tokenChange.emit(verified ? payload : null);
      this.readyChange.emit(verified);
    });
  };

  constructor(
    private translate: TranslateService,
    private zone: NgZone,
    private changeDetector: ChangeDetectorRef,
  ) {}

  async ngAfterViewInit(): Promise<void> {
    await this.initialize();
  }

  async retry(): Promise<void> {
    await this.initialize();
  }

  private async initialize(): Promise<void> {
    this.setStatus("loading");
    this.readyChange.emit(false);
    try {
      const language = this.altchaLanguage();
      // AppRouting references the reservation components eagerly. Load the
      // browser-ready distribution only when this widget is rendered so
      // CAPTCHA code cannot prevent unrelated routes such as /login from
      // bootstrapping. Importing the npm entry through Angular's Babel loader
      // injects a bare `require`, which browsers cannot execute.
      await this.loadScript("assets/vendor/altcha/altcha.js?v=3.2.1");
      await this.loadLanguage(language);

      if (this.destroyed) return;
      if (!globalThis.customElements) {
        throw new Error("Custom elements are not supported by this browser");
      }
      await Promise.race([
        globalThis.customElements.whenDefined("altcha-widget"),
        new Promise<never>((_, reject) =>
          setTimeout(
            () => reject(new Error("ALTCHA widget registration timed out")),
            8000,
          ),
        ),
      ]);
      if (this.destroyed) return;

      const element = this.widget.nativeElement;
      if (!this.listenerAttached) {
        element.addEventListener("statechange", this.stateChangeHandler);
        this.listenerAttached = true;
      }
      const configuration = element.configure({
        auto: "off",
        challenge: `${environment.baseUrl}/reservations/public/altcha/challenge/${encodeURIComponent(this.action)}`,
        display: "standard",
        hideFooter: true,
        hideLogo: true,
        language,
        name: "captcha_token",
        type: "checkbox",
        workers: 2,
      });
      this.setStatus("ready");
      Promise.resolve(configuration).catch((error) => {
        if (this.destroyed) return;
        console.error("[ALTCHA] Widget configuration failed:", error);
        this.setStatus("error");
        this.readyChange.emit(false);
      });
    } catch (error) {
      console.error("[ALTCHA] Widget failed to initialize:", error);
      this.setStatus("error");
      this.readyChange.emit(false);
    }
  }

  reset(): void {
    this.tokenChange.emit(null);
    this.readyChange.emit(false);
    const element = this.widget?.nativeElement;
    if (element && typeof element.reset === "function") {
      element.reset();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.listenerAttached) {
      this.widget.nativeElement.removeEventListener(
        "statechange",
        this.stateChangeHandler,
      );
    }
  }

  private async loadLanguage(language: string): Promise<void> {
    switch (language) {
      case "de":
        await this.loadScript("assets/vendor/altcha/i18n/de.js?v=3.2.1");
        break;
      case "el":
        await this.loadScript("assets/vendor/altcha/i18n/el.js?v=3.2.1");
        break;
      case "es-es":
        await this.loadScript("assets/vendor/altcha/i18n/es-es.js?v=3.2.1");
        break;
      case "tr":
        await this.loadScript("assets/vendor/altcha/i18n/tr.js?v=3.2.1");
        break;
    }
  }

  private loadScript(src: string): Promise<void> {
    const url = this.assetUrl(src);
    const existing = AltchaComponent.scriptLoads.get(url);
    if (existing) return existing;

    const load = new Promise<void>((resolve, reject) => {
      const script = document.createElement("script");
      script.src = url;
      script.async = true;
      script.addEventListener("load", () => resolve(), { once: true });
      script.addEventListener(
        "error",
        () => reject(new Error(`Failed to load ${url}`)),
        { once: true }
      );
      document.head.appendChild(script);
    });
    AltchaComponent.scriptLoads.set(url, load);
    load.catch(() => AltchaComponent.scriptLoads.delete(url));
    return load;
  }

  private assetUrl(path: string): string {
    // <base href="./"> changes as Angular navigates and would otherwise turn
    // this into /edit/:company/assets/.... Angular publishes these files in
    // the application's root assets directory.
    return new URL(`/${path.replace(/^\/+/, "")}`, location.origin).href;
  }

  private setStatus(status: "loading" | "ready" | "error"): void {
    if (this.destroyed) return;
    this.zone.run(() => {
      this.status = status;
      this.changeDetector.detectChanges();
    });
  }

  private altchaLanguage(): string {
    const language = (this.translate.currentLang || "en").toLowerCase();
    return language === "es" ? "es-es" : language;
  }
}
